import React, { Component } from 'react';
import { connect } from 'react-redux';

// Externals
import compose from 'recompose/compose';
import PropTypes from 'prop-types';
import validate from 'validate.js';

// Material helpers
import { withStyles } from '@material-ui/core/styles';
import withMobileDialog from '@material-ui/core/withMobileDialog';

// Material components
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import LinearProgress from '@material-ui/core/LinearProgress';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

// Material icons
import Add from '@material-ui/icons/Add';

// Shared services
import { addBike } from 'store/bike/actionsCreator';
import { fetchBikeTypes } from 'store/bikeType/actionsCreator';

// Translations
import translate from 'helpers/translate';

// Component styles
import styles from './styles';

// Form validation schema
import schema from './schema';

// Model
import { LockKindsLabel, LockKindsValues } from 'models/lockKind';

class AddBikeDialog extends Component {
  signal = true;

  state = {
    values: {
      name: '',
      type: '',
      description: '',
      padlockKind: '',
      padlockMac: '',
      imageUrl: '',
      imagePreview: ''
    },
    touched: {
      name: false,
      type: false,
      padlockKind: false,
      padlockMac: false
    },
    errors: {
      name: null,
      type: null,
      padlockKind: null,
      padlockMac: null
    },
    isValid: false,
    submitError: null
  };

  componentDidMount() {
    this.signal = true;
    this.props.fetchBikeTypes();
  }

  componentDidUpdate(prevProps) {
    // Typical usage (don't forget to compare props):
    if (this.props.currentOrganization !== prevProps.currentOrganization) {
      this.props.fetchBikeTypes();
    }
  }

  componentWillUnmount() {
    this.signal = false;
  }

  validateForm = () => {
    const { values } = this.state;

    const newState = { ...this.state };
    const errors = validate(values, schema);

    newState.errors = errors || {};
    newState.isValid = errors ? false : true;

    this.setState(newState);
  };

  handleChange = (field, value) => {
    const newState = { ...this.state };

    newState.submitError = null;
    newState.touched[field] = true;
    newState.values[field] = value;

    if (field === 'type') {
      let bikeType = this.props.bikeTypes.find((bt) => bt._id === value);
      newState.values['description'] = bikeType.description;
    }

    if (field === 'imageUrl') {
      newState.values['imagePreview'] = value;
    }

    this.setState(newState, this.validateForm);
  };

  handleAdd = async () => {
    const { values } = this.state;
    await this.props.addBike(values, (error) => {
      if (error) {
        this.setState({
          submitError: error
        });
      } else {
        let values = { ...this.state.values };
        values.name = '';
        values.type = '';
        values.description = '';
        values.imageUrl = '';
        values.padlockKind = '';
        values.padlockMac = '';
        values.imagePreview = '';
        let touched = { ...this.state.touched };
        touched.name = false;
        touched.type = false;
        touched.padlockKind = false;
        touched.padlockMac = false;
        let errors = { ...this.state.errors };
        errors.name = null;
        errors.type = null;
        errors.padlockKind = null;
        errors.padlockMac = null;
        this.setState({
          values,
          touched,
          errors,
          isValid: false,
          submitError: null
        });
        this.props.handleClose();
      }
    });
  };

  handleClose = () => {
    let values = { ...this.state.values };
    values.name = '';
    values.type = '';
    values.description = '';
    values.padlockKind = '';
    values.padlockMac = '';
    values.imageUrl = '';
    values.imagePreview = '';
    let touched = { ...this.state.touched };
    touched.name = false;
    touched.type = false;
    touched.padlockKind = false;
    touched.padlockMac = false;
    let errors = { ...this.state.errors };
    errors.name = null;
    errors.type = null;
    errors.padlockKind = null;
    errors.padlockMac = null;
    this.setState({
      values,
      touched,
      errors,
      isValid: false,
      submitError: null
    });
    this.props.handleClose();
  };

  render() {
    const {
      bikeTypes,
      classes,
      fullScreen,
      isBikeLoading,
      isBikeTypeLoading,
      open,
      openAddBikeTypeDialog,
      strings
    } = this.props;

    const { values, touched, errors, isValid, submitError } = this.state;

    const showNameError = touched.name && errors.name;
    const showTypeError = touched.type && errors.type;
    const showPadlockKindError = touched.padlockKind && errors.padlockKind;
    const showPadlockMacError = touched.padlockMac && errors.padlockMac;

    return (
      <Dialog
        aria-labelledby="form-dialog-title"
        fullScreen={fullScreen}
        fullWidth
        maxWidth="sm"
        onClose={this.handleClose}
        open={open}
      >
        <DialogTitle className={classes.dialogTitle} id="form-dialog-title">
          {strings.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{strings.subtitle}</DialogContentText>
          {isBikeLoading && <LinearProgress className={classes.dialogLoader} />}
          <form autoComplete="off" className={classes.dialogForm}>
            <Grid container spacing={24}>
              <Grid item xs={12}>
                <TextField
                  className={classes.textField}
                  error={showNameError ? true : false}
                  helperText={showNameError ? strings.name.error : ''}
                  label={strings.name.label}
                  margin="normal"
                  name="name"
                  onChange={(event) => this.handleChange('name', event.target.value)}
                  required
                  type="text"
                  value={values.name}
                  variant="outlined"
                />
                <TextField
                  InputProps={{
                    endAdornment: isBikeTypeLoading ? (
                      <InputAdornment position="end">
                        <CircularProgress />
                      </InputAdornment>
                    ) : (
                      <InputAdornment position="end">
                        <IconButton onClick={openAddBikeTypeDialog}>
                          <Add />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  SelectProps={{
                    MenuProps: {}
                  }}
                  className={classes.textField}
                  disabled={isBikeTypeLoading}
                  error={showTypeError ? true : false}
                  helperText={showTypeError ? strings.type.error : ''}
                  label={strings.type.label}
                  margin="normal"
                  name="type"
                  onChange={(event) => this.handleChange('type', event.target.value)}
                  required
                  select
                  type="text"
                  value={values.type}
                  variant="outlined"
                >
                  {bikeTypes.map((option) => (
                    <MenuItem key={option._id} value={option._id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  className={classes.textField}
                  disabled
                  label={strings.description.label}
                  margin="normal"
                  multiline
                  name="description"
                  rows="3"
                  rowsMax="3"
                  type="text"
                  value={values.description}
                  variant="outlined"
                />
                <TextField
                  SelectProps={{
                    MenuProps: {}
                  }}
                  className={classes.textField}
                  error={showPadlockKindError ? true : false}
                  helperText={showPadlockKindError ? strings.padlockKind.error : ''}
                  label={strings.padlockKind.label}
                  margin="normal"
                  name="padlockKind"
                  onChange={(event) => this.handleChange('padlockKind', event.target.value)}
                  required
                  select
                  type="text"
                  value={values.padlockKind}
                  variant="outlined"
                >
                  {LockKindsValues.map((kind) => (
                    <MenuItem key={kind} value={kind}>
                      {LockKindsLabel[kind]}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  className={classes.textField}
                  error={showPadlockMacError ? true : false}
                  helperText={showPadlockMacError ? strings.padlockMac.error : ''}
                  label={strings.padlockMac.label}
                  margin="normal"
                  name="padlockMac"
                  onChange={(event) => this.handleChange('padlockMac', event.target.value)}
                  required
                  type="text"
                  value={values.padlockMac}
                  variant="outlined"
                />
                <TextField
                  className={classes.textField}
                  label={strings.imageUrl.label}
                  margin="normal"
                  name="imageUrl"
                  onChange={(event) => this.handleChange('imageUrl', event.target.value)}
                  type="url"
                  value={values.imageUrl}
                  variant="outlined"
                />
                <img
                  alt=""
                  className={classes.imageField}
                  onError={() => {
                    let values = { ...this.state.values };
                    values.imagePreview = '/images/bike_placeholder.jpg';
                    this.setState({
                      values
                    });
                  }}
                  src={values.imagePreview ? values.imagePreview : '/images/bike_placeholder.jpg'}
                />
              </Grid>
            </Grid>
            {submitError && (
              <Typography className={classes.submitError} variant="body2">
                {strings.errors.unknownError}
              </Typography>
            )}
          </form>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={this.handleClose}>
            {strings.cancel}
          </Button>
          <Button color="primary" disabled={!isValid} onClick={this.handleAdd}>
            {strings.add}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

AddBikeDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  openAddBikeTypeDialog: PropTypes.func.isRequired,
  strings: PropTypes.object.isRequired
};

const mapStateToProps = (state) => {
  return {
    currentOrganization: state.organization.currentOrganization,
    isBikeLoading: state.bike.loading,
    isBikeTypeLoading: state.bikeType.loading,
    bikeTypes: state.bikeType.bikeTypes
  };
};

const mapDispatchToProps = {
  addBike,
  fetchBikeTypes
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  translate('AddBikeDialog'),
  withMobileDialog(),
  withStyles(styles)
)(AddBikeDialog);
